import type { InsightScopeTuple } from "../types/insightApi";

/**
 * Reconciles the scope the UI *asked for* with the scopes the source has
 * actually *processed* (CTX-424).
 *
 * Two different facts were being treated as one. `InsightScopeBrowser` passes
 * the reflector's Cloud/Account/Region ancestry in the BGP Visibility sidebar
 * tree straight into `activateScope` — that says **where the reflector
 * instance lives**. The digest behind `/insight/topology` is keyed by the
 * scope **discovery actually processed**, which can be a different account
 * entirely: production `CTX-2.1-TEST` lives in `940761539651` while its
 * digest describes `734722513437` and carries 112 nodes. Asking for the
 * former returned an empty `200` forever, with the artifact sitting in S3.
 *
 * `listTopologyScopes` is the only endpoint that knows the processed set. It
 * shipped with CTX-97 as a "documented, tested contract shape in case a
 * future health/freshness enrichment pass wants them" (see the header of
 * `services/insightApi.ts`) — this is that pass.
 */

/** How the requested scope relates to what the source has processed. */
export type ScopeResolution =
	/** The requested scope is one the source processed — the healthy path, unchanged. */
	| { kind: "match"; scope: InsightScopeTuple }
	/**
	 * The request did not match, but the source has exactly one processed
	 * scope, so there is no ambiguity about what the user meant to see.
	 */
	| { kind: "redirected"; scope: InsightScopeTuple; requested: InsightScopeTuple }
	/** Several processed scopes and none matches — only the user can pick. */
	| { kind: "ambiguous"; scopes: InsightScopeTuple[] }
	/** The source has processed nothing; no request can succeed. */
	| { kind: "none" };

/**
 * Compares by identity rather than by formatting. The manifest and the
 * querystring are written by different producers, so a scope must not fail to
 * match on case or padding alone — the same normalization the backend's
 * `canonicalizeProvider` has always applied to `cloud`.
 */
function canonical(value: string | null | undefined): string | null {
	if (value === null || value === undefined) return null;
	const trimmed = String(value).trim();
	return trimmed === "" ? null : trimmed.toLowerCase();
}

function sameScope(a: InsightScopeTuple, b: InsightScopeTuple): boolean {
	if (canonical(a.provider) !== canonical(b.provider)) return false;
	if (canonical(a.region) !== canonical(b.region)) return false;
	// A null account on either side means "not scoped by account" and matches
	// whatever the other side carries; two concrete accounts must be equal.
	if (a.account == null || b.account == null) return true;
	return canonical(a.account) === canonical(b.account);
}

/**
 * `scopes` is what `listTopologyScopes` returned for the active source.
 * `requested` is the scope currently in redux/the URL, or `null` while the
 * selection is still incomplete.
 */
export function resolveProcessedScope(
	requested: InsightScopeTuple | null,
	scopes: InsightScopeTuple[] | null | undefined
): ScopeResolution {
	const processed = (scopes ?? []).filter((scope) => scope && scope.provider && scope.region);
	if (processed.length === 0) return { kind: "none" };

	if (requested) {
		const matched = processed.find((scope) => sameScope(scope, requested));
		// Return the *requested* tuple, not the processed one it matched. They are
		// equivalent by `sameScope`, but the processed side is canonical
		// (`aws`) while the inventory gives `AWS` — handing back the processed
		// spelling changed the request's primitives and cost a second, identical
		// fetch on the healthy path. Verified against production: the log showed
		// `cloud=AWS` followed by `cloud=aws` for the same scope.
		if (matched) return { kind: "match", scope: requested };
	}

	if (processed.length === 1) {
		// No requested scope yet (fresh selection) or it pointed somewhere the
		// source never processed — either way there is only one thing to show.
		return requested
			? { kind: "redirected", scope: processed[0], requested }
			: { kind: "match", scope: processed[0] };
	}

	return { kind: "ambiguous", scopes: processed };
}

/** Human-readable `cloud / region / account` for messages and labels. */
export function formatScope(scope: InsightScopeTuple): string {
	return [scope.provider, scope.region, scope.account].filter(Boolean).join(" / ");
}
